import { useEffect } from "react";
import Head from "next/head";
import Image from "components/Image";
import Nav from "./Nav";
import styles from "./NotFoundPage.module.scss";
import lazy from "utils/lazy";
import { useQuery, gql } from "utils/urql";
import {
  NotFoundMostPopularQuery,
  NotFoundMostPopularFragment,
} from "types/api";
import explorers from "assets/images/404-explorers.png";
import { trackErrorPageView } from "utils/analytics";

const Footer = lazy(() => import("./Footer"));

const NotFoundMostPopularWrapper = () => {
  const [{ data }] = useQuery<NotFoundMostPopularQuery>({
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    query: NOT_FOUND_MOST_POPULAR_QUERY,
  });

  if (data?.mostPopular) {
    return <NotFoundMostPopular {...data.mostPopular} />;
  }

  return null;
};

const NotFoundMostPopular = ({
  river: { edges },
}: NotFoundMostPopularFragment) => {
  return (
    <>
      {edges.map((edge) => {
        return (
          <li className={styles.mostPopularItem} key={edge.node.id}>
            <a
              href={edge.node.url}
              dangerouslySetInnerHTML={{ __html: edge.node.title }}
            />
            {edge.node.authors.map((author) => {
              return (
                <a
                  className={styles.author}
                  key={author.id}
                  href={author.url}
                  dangerouslySetInnerHTML={{
                    __html: author.displayName,
                  }}
                />
              );
            })}
          </li>
        );
      })}
    </>
  );
};

const NotFoundPage = () => {
  useEffect(() => {
    trackErrorPageView({
      title: "Page Not Found (404)",
      statusCode: 404,
    });
  }, []);

  return (
    <>
      <Head>
        <title>Page Not Found (404) - The Atlantic</title>
      </Head>
      <Nav />
      <main className={styles.main} id="main-content">
        <div className={styles.root}>
          <section className={styles.top}>
            <Image
              className={styles.img}
              src={explorers}
              alt=""
              lazy={false}
              preload
            />
            <h1 className={styles.hed}>Page Not Found</h1>
            <div className={styles.text}>
              <p className={styles.dek}>
                We can’t find the page you’re looking for. It has likely either
                moved or no longer exists, or the website address is incorrect.
              </p>
              <p className={styles.dek}>We’re sorry for any inconvenience.</p>
              <a className={styles.backLink} href="/">
                Back to The Atlantic
              </a>
              <p className={styles.customerCare}>
                For further assistance,{" "}
                <a
                  className={styles.customerCareLink}
                  href="https://support.theatlantic.com/hc/en-us/requests/new"
                >
                  contact our Customer Care team
                </a>
              </p>
            </div>
          </section>
          <section>
            <h2 className={styles.mostPopularTitle}>Most Popular</h2>
            <ol className={styles.mostPopular}>
              <NotFoundMostPopularWrapper />
            </ol>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

NotFoundPage.fragment = gql`
  fragment notFoundMostPopularAuthor on Author {
    id
    displayName
    url
  }

  fragment notFoundMostPopularPromoItem on PromoItem {
    id
    url
    title
    authors {
      ...notFoundMostPopularAuthor
    }
  }

  fragment notFoundMostPopular on CuratedGroup {
    river(first: 4) {
      edges {
        cursor
        node {
          ...notFoundMostPopularPromoItem
        }
      }
    }
  }
`;

const NOT_FOUND_MOST_POPULAR_QUERY = gql`
  ${NotFoundPage.fragment}

  query NotFoundMostPopular {
    mostPopular {
      ...notFoundMostPopular
    }
  }
`;

export default NotFoundPage;
